import { Alert, Box, type Breakpoint } from '@mui/material';
import { type ReactNode, useState } from 'react';

import { ValidationData } from '@components/MailpieceActions/types';
import { DialogConfirmation } from '@uiKit/DialogConfirmation';

import { useValidationExitGuard } from '../../hooks/useValidationExitGuard';
import NotifyMairoomComment from './NotifyComment';
import useNotifyComment from './NotifyComment/useNotifyComment';

type Step = 'info' | 'action';

interface ActionWithInfoChildProps {
  step: Step;
  skipNotifyRecipient: boolean;
  comment: string;
  notifyMailroom: boolean;
  onClose: () => void;
  onFinish: () => void;
  onBackToInfo: (warning?: string) => void;
}

interface ActionWithInfoProps {
  open: boolean;
  title: string;
  infoContent: ReactNode;
  onClose: () => void;
  maxWidth?: Breakpoint;
  acceptButtonTitle?: string;
  cancelButtonTitle?: string;
  loading?: boolean;
  validationData?: ValidationData;
  onAccept?: (state: { skipNotifyRecipient: boolean; comment: string; notifyMailroom: boolean }) => Promise<void>;
  children: (props: ActionWithInfoChildProps) => ReactNode;
}

export const ActionWithInfo = ({
  open,
  title,
  infoContent,
  onClose,
  maxWidth = 'sm',
  acceptButtonTitle = 'Continue',
  cancelButtonTitle = 'Cancel',
  loading,
  validationData,
  onAccept,
  children,
}: ActionWithInfoProps) => {
  const [step, setStep] = useState<Step>('info');
  const [warning, setWarning] = useState<string | null>(null);

  const { values, changeValues, resetValues } = useNotifyComment();

  const handleClose = () => {
    setStep('info');
    resetValues();
    setWarning(null);
    onClose();
  };

  const { guardedExit, exitConfirmDialog } = useValidationExitGuard(validationData, handleClose);

  const handleAccept = async () => {
    if (onAccept) {
      await onAccept(values);
    } else {
      setWarning(null);
      setStep('action');
    }
  };

  const handleBackToInfo = (warningMessage?: string) => {
    setStep('info');
    setWarning(warningMessage || null);
  };

  return (
    <>
      <DialogConfirmation
        open={open && step === 'info'}
        maxWidth={maxWidth}
        title={title}
        content={
          <Box>
            {infoContent}
            {warning && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {warning}
              </Alert>
            )}

            <NotifyMairoomComment values={values} onChange={changeValues} />
          </Box>
        }
        acceptButtonHandleClick={handleAccept}
        loading={loading}
        cancelButtonHandleClick={guardedExit}
        acceptButtonTitle={acceptButtonTitle}
        cancelButtonTitle={cancelButtonTitle}
      />

      {exitConfirmDialog}

      {children({
        step,
        ...values,
        onClose: guardedExit,
        onFinish: handleClose,
        onBackToInfo: handleBackToInfo,
      })}
    </>
  );
};
